import { getStreamDisplayName, getStreamStatus, hasActiveStreamAccess } from 'src/utils/leadStreams';
import { streamIdFromPath } from 'src/utils/streamUrl';

import { streamPath } from './model';

import type { Stream } from 'src/services/LeadStreamsService/agent';
import type { CompanionReply } from './model';

export const isDeliveryQuestion = (question: string) =>
    /нет\s+(новых\s+)?(лидов|заявок)|(?:мало|ноль)\s+(?:лидов|заявок)|(?:не|перестали)\s+(?:приход|поступ)|ничего\s+не\s+(?:находит|приходит)/i.test(
        question
    );

export function streamIdFromPage(pathname: string, search: string): number | undefined {
    const code = pathname.match(/^\/leadstream\/([^/]+)\/?$/)?.[1];
    if (code) return streamIdFromPath(code) || undefined;
    const raw = pathname === '/feed' ? new URLSearchParams(search).get('stream') : null;
    return raw && /^\d+$/.test(raw) && Number.isSafeInteger(Number(raw)) && Number(raw) > 0 ? Number(raw) : undefined;
}

export function requestedStreamId(question: string, pageId?: number): number | undefined {
    const explicit = question.match(/(?:поток(?:е|а|у)?|№)\s*#?\s*(\d+)(?!\d)/i)?.[1];
    return explicit && Number.isSafeInteger(Number(explicit)) && Number(explicit) > 0 ? Number(explicit) : pageId;
}

/** A fresh configuration snapshot can explain a stop, but cannot prove source health. */
export function diagnoseDelivery(streams: Stream[], streamId?: number, checkedAt = new Date()): CompanionReply {
    const checked = `Проверка: ${checkedAt.toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })}.`;
    const stream = streamId ? streams.find((s) => s.id === streamId) : streams.length === 1 ? streams[0] : undefined;
    if (!stream) {
        if (streamId) return { text: `${checked} Поток №${streamId} недоступен. Не удалось проверить его состояние.` };
        if (!streams.length)
            return {
                text: `${checked} Потоков пока нет. Настройте первый поиск, чтобы получать лиды.`,
                actions: [{ label: 'Настроить поток', path: '/leadstream' }],
            };
        return {
            text: 'Какой поток проверить? Откройте его и спросите «Почему нет лидов?».',
            actions: streams.slice(0, 6).map((s) => ({ label: getStreamDisplayName(s), path: streamPath(s) })),
        };
    }
    const prefix = `${checked}\n«${getStreamDisplayName(stream)}». `;
    const open = { label: 'Открыть поток', path: streamPath(stream) };
    if (stream.category.is_delivery_enabled === false || stream.category.is_active === false)
        return {
            text: `${prefix}Выдача по этой категории отключена. Уточните у поддержки, когда она возобновится. Пополнение само по себе её не включит.`,
            actions: [open],
        };
    const status = getStreamStatus(stream).key;
    const access = hasActiveStreamAccess(stream);
    const capacity = stream.balance > 0 || access;
    if (
        (status === 'active' && (!stream.is_active || !capacity)) ||
        (status === 'paused' && (stream.is_active || !capacity)) ||
        ((status === 'ended' || status === 'pending') && capacity)
    )
        return {
            text: `${prefix}Статус и остаток не согласованы. По этим данным нельзя определить причину. Откройте поток и обновите его состояние.`,
            actions: [open],
        };
    if (status === 'paused')
        return {
            text: `${prefix}Поиск на паузе. ${
                access ? 'Доступ ещё действует.' : `Остаток лидов: ${stream.balance}.`
            } Возобновите поток в его настройках.`,
            actions: [{ ...open, label: 'Управление потоком' }],
        };
    if (status === 'ended')
        return {
            text: `${prefix}Пакет закончился: доступ не действует, лидов на остатке нет. Выберите пополнение, чтобы продолжить получение.`,
            actions: [{ label: 'Выбрать пополнение', path: `/leadstream/${stream.id}#checkout` }],
        };
    if (status === 'pending')
        return {
            text: `${prefix}Поток ещё не запущен. Завершите настройку и выберите стартовый пакет.`,
            actions: [{ ...open, label: 'Продолжить запуск' }],
        };
    return {
        text: `${prefix}Поток включён. ${
            access ? 'Доступ действует.' : `Остаток лидов: ${stream.balance}.`
        } Паузы или нехватки объёма нет.\nПричина отсутствия новых лидов пока не известна: состояние источников в этой проверке недоступно. Проверьте правила отбора; если они верны, обратитесь в поддержку.`,
        actions: [
            { label: 'Правила отбора', path: `/leadstream/${stream.id}#rules` },
            { label: 'Лиды потока', path: `/feed?stream=${stream.id}&from=companion` },
        ],
    };
}
